
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, BookOpen, Headphones, Image, PlayCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { AIHybridService } from "@/utils/aiHybridService";

interface MultiModalContentViewProps {
  content: string;
  topics: string[];
  preferredStyle?: string;
}

export const MultiModalContentView: React.FC<MultiModalContentViewProps> = ({
  content,
  topics,
  preferredStyle = 'visual'
}) => {
  const [activeTab, setActiveTab] = useState<string>(preferredStyle === 'auditory' ? 'audio' : preferredStyle === 'visual' ? 'visual' : 'text');
  const [audioContent, setAudioContent] = useState<string | null>(null);
  const [visualSummary, setVisualSummary] = useState<string | null>(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [loadingVisual, setLoadingVisual] = useState(false);
  const [summaryPoints, setSummaryPoints] = useState<string[]>([]);
  
  // Extract main points from content for summaries
  useEffect(() => {
    const extractMainPoints = () => {
      // Basic extraction of points from HTML content
      const strippedContent = content.replace(/<[^>]+>/g, '');
      
      // Split by sentences or bullet points
      const sentences = strippedContent.split(/[.•]\s+/);
      
      // Filter for sentences that contain key information
      const keyPoints = sentences.filter(sentence => {
        const s = sentence.trim().toLowerCase();
        return s.length > 30 && 
               (s.includes('important') || 
                s.includes('key') || 
                s.includes('significant') || 
                s.includes('critical') ||
                topics.some(topic => s.includes(topic.toLowerCase())));
      });
      
      // Limit to 5 main points
      setSummaryPoints(keyPoints.slice(0, 5).map(p => p.trim()));
    };
    
    extractMainPoints();
  }, [content, topics]);
  
  const generateAudioSummary = async () => {
    if (audioContent) return; // Already generated
    
    setLoadingAudio(true);
    try {
      // Summary to convert to audio
      const summary = `Here's a summary of the key topics in current affairs: ${summaryPoints.join('. ')}`;
      
      // Call the edge function to generate audio
      const { data, error } = await supabase.functions.invoke('ai-tutoring', {
        body: {
          action: 'text_to_speech',
          text: summary
        }
      });
      
      if (error) throw error;
      
      if (data?.audioUrl) {
        setAudioContent(data.audioUrl);
      } else {
        throw new Error('No audio generated');
      }
    } catch (error) {
      console.error('Error generating audio summary:', error);
      toast.error('Failed to generate audio summary');
    } finally {
      setLoadingAudio(false);
    }
  };
  
  const generateVisualSummary = async () => {
    if (visualSummary) return; // Already generated
    
    setLoadingVisual(true);
    try {
      // Create a visual summary using the AIHybridService
      const mainTopic = topics[0] || 'Current Affairs';
      const visualSummaryText = await AIHybridService.processText(
        mainTopic, 
        'mindmap'
      );
      
      setVisualSummary(visualSummaryText);
    } catch (error) {
      console.error('Error generating visual summary:', error);
      toast.error('Failed to generate visual summary');
    } finally {
      setLoadingVisual(false);
    }
  };
  
  useEffect(() => {
    // Auto-load the preferred content type
    if (activeTab === 'audio') {
      generateAudioSummary();
    } else if (activeTab === 'visual') {
      generateVisualSummary();
    }
  }, [activeTab]);
  
  return (
    <Card className="bg-white">
      <CardContent className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-3 mb-4">
            <TabsTrigger value="text" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" /> Text
            </TabsTrigger>
            <TabsTrigger value="audio" className="flex items-center gap-2">
              <Headphones className="h-4 w-4" /> Audio
            </TabsTrigger>
            <TabsTrigger value="visual" className="flex items-center gap-2">
              <Image className="h-4 w-4" /> Visual
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="text">
            <div className="prose prose-sm max-w-none">
              <div className="space-y-2 py-2">
                <h3 className="text-lg font-medium">Key Points</h3>
                <ul className="list-disc pl-5 space-y-1">
                  {summaryPoints.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </div>
              <hr className="my-4" />
              <div dangerouslySetInnerHTML={{ __html: content }} />
            </div>
          </TabsContent>
          
          <TabsContent value="audio">
            <div className="space-y-4">
              {loadingAudio ? (
                <div className="flex flex-col items-center justify-center p-12 border rounded-lg bg-slate-50">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                  <p className="text-muted-foreground">Generating audio summary...</p>
                </div>
              ) : audioContent ? (
                <div className="border rounded-lg p-4">
                  <h3 className="text-lg font-medium mb-3">Audio Summary</h3>
                  <audio controls className="w-full">
                    <source src={audioContent} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                  <div className="mt-4 text-sm text-muted-foreground">
                    <p>This audio summary covers the key points from your current affairs materials.</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-12 border rounded-lg bg-slate-50">
                  <Button onClick={generateAudioSummary}>
                    <PlayCircle className="h-5 w-5 mr-2" />
                    Generate Audio Summary
                  </Button>
                </div>
              )}
              
              <div className="prose prose-sm max-w-none border-t pt-4 mt-4">
                <h4 className="text-sm font-medium mb-2">Text Transcript</h4>
                <ul className="list-disc pl-5 space-y-1">
                  {summaryPoints.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="visual">
            <div className="space-y-4">
              {loadingVisual ? (
                <div className="flex flex-col items-center justify-center p-12 border rounded-lg bg-slate-50">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                  <p className="text-muted-foreground">Creating visual summary...</p>
                </div>
              ) : visualSummary ? (
                <div className="border rounded-lg p-4">
                  <h3 className="text-lg font-medium mb-3">Visual Mind Map</h3>
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: visualSummary }}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-12 border rounded-lg bg-slate-50">
                  <Button onClick={generateVisualSummary}>
                    <Image className="h-5 w-5 mr-2" />
                    Generate Visual Summary
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
